import {
	createSlice,
	createSelector,
	createAsyncThunk,
} from "@reduxjs/toolkit";
import dayjs from "dayjs";
import { newId } from "utils/common";

const defaultDate = dayjs().format("YYYY-MM-DD");

export const getRequestForm = () => {
	return {
		id: newId(),
		requestFormId: 0,
		formId: 0,
		parentFormId: 0,
		formTypeId: 0, //must be defined by RequestForm component
		title: "New Draft Form",
		startDate: defaultDate,
		endDate: "9999-12-31",
		statusId: 1, //defaults to draft
		personId: null,
		businessUnitId: 0,
		companyId: 0,
		isDeleted: false,
		confirmationStep1: null,
		isPersonConsentGiven: null,
		leaveComments: null,
		supplementaryData: null,
	};
};

export const validateRequestForm = createAsyncThunk(
	"requestForm/validateRequestForm",
	async (_, { dispatch, getState }) => {
		const state = getState();
		const {
			title,
			hasCourseRequest,
			leaveFormId,
			details,
			location,
			formTypeId,
			parentFormId,
			startDate,
		} = selectRequestFormData(state);

		//general errors
		let errors = [];
		if (!title || title === "") errors.push("Form name is required");

		//leave section errors
		const isLeaveSectionMandatory =
			dayjs(startDate).format("YYYY-MM-DD") >= "2024-07-15";
		const isCourseClaim = formTypeId === 2 && !parentFormId;
		if (isLeaveSectionMandatory) {
			if ([null, undefined].includes(hasCourseRequest) && isCourseClaim)
				errors.push(
					"Please indicate if CME leave has been approved for this form"
				);
			if (hasCourseRequest && !leaveFormId && isCourseClaim)
				errors.push(
					"Please select an approved or finalised CME leave request"
				);

			if (!hasCourseRequest && isCourseClaim && (!location || !details))
				errors.push("Please fill out conference details and location");
		}

		dispatch(validationErrorsEdited(errors.length));
		dispatch(validationErrorListEdited(errors));
		return errors.length;
	}
);

const initialState = {
	status: "Draft",
	state: {
		isNew: null,
		isLocked: false,
	},
	session: {
		isSilentSave: false,
		currentPage: undefined,
		status: "starting",
	},
	validationErrors: 0,
	validationErrorList: [],
	error: null,
	data: {
		...getRequestForm(),
	},
};

const requestFormSlice = createSlice({
	name: "requestForm",
	initialState,
	reducers: {
		validationErrorsEdited(state, action) {
			state.validationErrors = action.payload;
		},
		validationErrorListEdited(state, action) {
			state.validationErrorList = action.payload;
		},
		requestFormReceived(state, action) {
			state.data = action.payload;
		},
		requestFormEdited(state, action) {
			const { id, value } = action.payload;
			state.data[id] = value;
		},
		hasCourseRequestEdited(state, action) {
			//if we previously had a value here, we should clear related data
			if (![undefined, null].includes(state.data.hasCourseRequest)) {
				state.data.leaveFormId = null;
				state.data.location = null;
				state.data.details = null;
			}
			state.data.hasCourseRequest = action.payload;
		},
		leaveFormSelected(state, action) {
			const { formId, courseDetails, courseLocation } = action.payload;
			state.data.leaveFormId = formId;
			state.data.location = courseLocation;
			state.data.details = courseDetails;
		},
		sessionEdited(state, action) {
			state.session = action.payload;
		},
		stateEdited(state, action) {
			state.state = action.payload;
		},
	},
	extraReducers: {
		RESET_CME: (state, action) => {
			return initialState;
		},
	},
});

export const {
	requestFormEdited,
	sessionEdited,
	stateEdited,
	requestFormReceived,
	validationErrorListEdited,
	validationErrorsEdited,
	hasCourseRequestEdited,
	leaveFormSelected,
} = requestFormSlice.actions;

export const selectData = (state) => state.cme.requestForm.data;
export const selectRequestFormData = createSelector(
	(state) => state.cme.requestForm.data,
	(data) => data
);
export const selectIsPersonConsentGiven = (state) =>
	state.cme.requestForm.data.isPersonConsentGiven;

export const selectRequestFormId = createSelector(
	[
		(state) => state.form?.definition?.name === "CPD Course Claim",
		(state) => state.cme,
	],
	(isCpdCourseClaim, cme) => {
		return isCpdCourseClaim
			? cme.cpeForm.data.formId
			: cme.requestForm.data.formId;
	}
);

export const selectRequestFormStatus = (state) =>
	state.cme.requestForm.session.status;
export const selectRequestFormLeaveComments = (state) =>
	state.cme.requestForm.data.leaveComments;
export const selectRequestFormSupplementaryDataActiveCpe = (state) => {
	return getActiveCpeFromSupplementaryData(
		state.cme.requestForm.data.supplementaryData
	);
};
export const selectRequestFormTypeId = (state) =>
	state.cme.requestForm.data.formTypeId;
export const selectRequestFormValidationErrorList = (state) =>
	state.cme.requestForm.validationErrorList;

export const getActiveCpeFromSupplementaryData = (supplementaryData) => {
	var obj = JSON.parse(supplementaryData ?? null);
	return obj?.activeCpe;
};

export default requestFormSlice.reducer;
